import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DocumentsPermission } from '@idc-forms/permissions'
import { Eye, EyeOff, GripVertical, ImageOff, ImagePlus, Images, X } from 'lucide-react'
import { memo, useEffect, useMemo, useState } from 'react'
import { CapImage } from '@/features/client-app-preview/components/cap-image'
import { CapText } from '@/features/client-app-preview/components/cap-text'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'
import { createQueue, markItem, type UploadQueueItem } from '../domain/upload-queue'
import {
  useDocumentCategoriesQuery,
  useDocumentThumbnailQuery,
  useOwnerDocumentsQuery,
  useReorderDocumentSlotMutation,
  useSetDocumentSlotMutation,
  useSetDocumentVisibilityMutation,
  useUploadDocumentsMutation
} from '../hooks/use-owner-documents'
import {
  CATALOG_PUBLISH_REACH,
  type CatalogOwnerType,
  type DocumentOwnerType
} from '../models/document-owner'
import { DocumentUploadDialog } from './document-upload-dialog'
import { PortfolioCanvas } from './portfolio-canvas'

/**
 * LA GALERÍA de un nivel del catálogo: las fotos que la app pinta en la portada.
 *
 * ── POR QUÉ ES OTRA PESTAÑA Y NO UN FILTRO DEL CARTAPACIO ────────────────────
 *
 * Porque el trabajo es otro. En el cartapacio se clasifica y se publica; acá se decide qué se ve
 * y EN QUÉ ORDEN, que es trabajo visual y se hace mirando. Comparte la MISMA consulta
 * (`useOwnerDocumentsQuery`) y filtra en memoria: son dos vistas del mismo conjunto, no dos
 * conjuntos, así que subir una foto desde acá la deja disponible allá sin sincronizar nada.
 *
 * ── LAS MINIATURAS SON EL DERIVADO, NO EL ORIGINAL ───────────────────────────
 *
 * ☠️ Antes cada celda pintaba el ORIGINAL: las fotos reales son de 8750 × 4063 —35 megapíxeles,
 * ~142 MB de bitmap cada una— dibujadas en ~150 px. Doce de esas son ~1,7 GB de bitmaps que el
 * navegador tiene que decodificar y retener para acomodar un carrusel. `decoding="async"` lo
 * mitigaba y nunca lo arregló. Ahora se pide la hoja generada en WebP (768 × 1024), que es el
 * mismo archivo que recibe la app del cliente.
 *
 * ── LA GRILLA SIGUE EL ANCHO DEL PANEL, NO EL DE LA VENTANA ──────────────────
 *
 * Dos columnas, tres a partir de 420 px y cuatro a partir de 560. Con `@container` y no con media
 * queries: el panel se arrastra entre 320 y 600 px sin que el viewport cambie, así que una media
 * query se quedaría en dos columnas a 600 px — que es exactamente lo que pasaba.
 *
 * ── LO QUE LA PANTALLA TIENE QUE DEJAR CLARO ─────────────────────────────────
 *
 * Que estar en la galería NO es estar publicado. Son dos actos con dos permisos, y una foto en el
 * slot pero interna no la ve nadie. Por eso cada miniatura lleva su estado encima.
 */
type DocumentGalleryTabProps = {
  ownerType: DocumentOwnerType
  ownerId: string
  /**
   * Cómo se llama esto para quien lo mira. En las tipologías es el CÓDIGO (`APT1`, `VB1`) y no el
   * nombre: tres de los cuatro proyectos tienen ocho o diez tipologías llamadas todas
   * «APARTAMENTO», así que sin el código un asesor no sabe a cuál le está subiendo las fotos.
   */
  ownerLabel?: string
  /**
   * Acciones extra en el encabezado, junto a «Subir foto».
   *
   * Es un hueco y no una lista de props booleanas: quien monta esta pestaña decide qué más se
   * puede hacer con la galería de SU nivel —hoy, copiar las fotos desde otro— sin que este
   * componente tenga que conocer esa función ni sus permisos. Filosofía shadcn: composición, no
   * un parámetro por diferencia.
   */
  extraActions?: React.ReactNode
}

/** El máximo que el backend acepta en el slot. Se dice acá para no ofrecer lo imposible. */
const GALLERY_LIMIT = 12

type GalleryPhoto = {
  id: string
  name: string
  mimeType: string | null
  visibility: 'public' | 'internal'
  slotOrder: number | null
  hasThumbnail: boolean
}

/**
 * Una foto de la galería: la miniatura, su estado y el asa para reordenar.
 *
 * ── EL ASA VA SOBRE LA IMAGEN, Y ES UN BOTÓN DE VERDAD ───────────────────────
 *
 * En una tarjeta de foto el asa tiene que estar donde está la foto; sacarla afuera le robaría
 * ancho a lo único que importa ver. Y es un `<button>` y no un `div` para que el orden se pueda
 * cambiar con el teclado, que es lo único que tiene quien no usa el mouse — la guía es explícita:
 * un gesto de arrastre necesita siempre una alternativa.
 */
const SortablePhoto = memo(function SortablePhoto({
  photo,
  index,
  canPublish,
  onRemove,
  onToggleVisibility,
  disabled
}: {
  photo: GalleryPhoto
  index: number
  canPublish: boolean
  onRemove: (id: string) => void
  onToggleVisibility: (photo: GalleryPhoto) => void
  disabled: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
    disabled
  })
  const published = photo.visibility === 'public'
  const thumbnail = useDocumentThumbnailQuery(photo.id, photo.hasThumbnail)
  const source = thumbnail.data?.urls[0] ?? null

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex min-w-0 flex-col gap-1',
        // `will-change-transform` promueve la tarjeta a su propia capa de composición: el
        // arrastre pasa a ser un transform de una textura YA rasterizada en vez de un repintado.
        // Va sólo mientras se arrastra —una capa permanente por tarjeta gasta memoria de GPU para
        // nada— y `contain-paint` evita que el repintado de una se propague a las vecinas.
        'contain-paint',
        isDragging && 'z-10 opacity-80 will-change-transform'
      )}
    >
      <div
        className={cn(
          'cap-border-neutral-200 relative aspect-square w-full overflow-hidden rounded-[var(--cap-radius-lg)] border',
          isDragging && 'shadow-lg'
        )}
      >
        {source ? (
          // `alt` vacío A PROPÓSITO: el nombre del archivo ya se anuncia en el título de abajo, y
          // repetirlo haría que un lector de pantalla lea dos veces lo mismo. El nombre tampoco
          // describe la foto, así que no hay texto alternativo real que dar.
          <CapImage src={source} alt="" className="h-full w-full" />
        ) : thumbnail.isLoading ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : (
          // Honesto: la miniatura todavía no se generó (o el asset es anterior al generador). La
          // foto existe; lo que falta es su derivado, y el backfill lo produce.
          <div className="cap-bg-surface-cover flex h-full w-full items-center justify-center">
            <ImageOff className="cap-fg-neutral-300 h-6 w-6" aria-hidden />
          </div>
        )}

        <button
          type="button"
          className="absolute top-1 left-1 z-20 cursor-grab rounded-sm bg-background/80 p-0.5 text-muted-foreground backdrop-blur-sm active:cursor-grabbing disabled:cursor-not-allowed"
          disabled={disabled}
          aria-label={`Mover ${photo.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        {/* La portada se DICE encima de la foto, no se deduce de la posición: quien acomoda tiene
            que ver cuál va a abrir la galería sin ponerse a contar celdas. */}
        {index === 0 ? (
          <span className="absolute bottom-1 left-1 z-20 rounded-sm bg-foreground/80 px-1.5 py-0.5 text-[10px] font-medium text-background">
            Portada
          </span>
        ) : null}

        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          className="absolute top-1 right-1 z-20 bg-background/80 backdrop-blur-sm transition-transform duration-150 ease-out active:scale-[0.97]"
          aria-label={`Quitar ${photo.name} de la galería`}
          onClick={() => {
            onRemove(photo.id)
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <CapText variant="body" weight="bold" truncate title={photo.name}>
        {photo.name}
      </CapText>
      {/* El estado se dice DOS veces —rótulo y color— porque es lo que más caro se paga leer mal:
          una foto en la galería pero interna no la ve nadie. */}
      <span
        className={cn(
          'truncate text-[11px]',
          published
            ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-amber-700 dark:text-amber-400'
        )}
      >
        {published ? 'Publicada' : 'Sin publicar'}
      </span>

      {canPublish ? (
        <Button
          type="button"
          variant={published ? 'ghost' : 'outline'}
          size="sm"
          className="mt-0.5 h-6 w-full gap-1 text-xs transition-transform duration-150 ease-out active:scale-[0.97]"
          onClick={() => {
            onToggleVisibility(photo)
          }}
        >
          {published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {published ? 'Despublicar' : 'Publicar'}
        </Button>
      ) : null}
    </li>
  )
})

export function DocumentGalleryTab({
  ownerType,
  ownerId,
  ownerLabel,
  extraActions
}: DocumentGalleryTabProps) {
  const { has } = usePermissions()
  const canPublish = has(DocumentsPermission.PublishCatalogDocuments)

  const query = useOwnerDocumentsQuery({ ownerType, ownerId })
  const setSlot = useSetDocumentSlotMutation(ownerType, ownerId)
  const reorder = useReorderDocumentSlotMutation(ownerType, ownerId)
  const setVisibility = useSetDocumentVisibilityMutation(ownerType, ownerId)
  const [queue, setQueue] = useState<UploadQueueItem[]>([])
  // `slot: 'gallery'` hace que cada foto entre al carrusel al subirla, en el MISMO acto y en el
  // orden en que se eligieron. Subir y después tener que arrastrarlas sería el mismo trabajo en
  // dos pasos, y el segundo se olvida.
  const upload = useUploadDocumentsMutation(ownerType, ownerId, {
    slot: 'gallery',
    onItemChange: (key, patch) => {
      setQueue((current) => markItem(current, key, patch as Partial<Omit<UploadQueueItem, 'key'>>))
    }
  })
  const categories = useDocumentCategoriesQuery()
  const [uploadOpen, setUploadOpen] = useState(false)

  const documents = useMemo(() => query.data ?? [], [query.data])

  const serverPhotos = useMemo<GalleryPhoto[]>(
    () =>
      documents
        .filter((document) => document.slot === 'gallery')
        .map((document) => ({
          id: document.id,
          name: document.name,
          mimeType: document.mimeType,
          visibility: document.visibility,
          slotOrder: document.slotOrder,
          hasThumbnail: document.hasThumbnail
        }))
        .sort((left, right) => (left.slotOrder ?? 0) - (right.slotOrder ?? 0)),
    [documents]
  )

  /** Sólo las IMÁGENES que todavía no están en la galería. El backend rechaza el resto. */
  const candidates = useMemo(
    () =>
      documents.filter(
        (document) => document.slot !== 'gallery' && (document.mimeType ?? '').startsWith('image/')
      ),
    [documents]
  )

  // El orden vive en el componente mientras se arrastra: sin esto la grilla volvería al orden del
  // servidor en cada render y la foto se escaparía del cursor.
  const [order, setOrder] = useState<GalleryPhoto[]>(serverPhotos)
  useEffect(() => {
    setOrder(serverPhotos)
  }, [serverPhotos])

  const sensors = useSensors(
    // Un umbral de 6 px: sin él, un click sobre el asa arranca un arrastre de cero píxeles y se
    // come el click de los botones vecinos.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = order.findIndex((photo) => photo.id === active.id)
    const to = order.findIndex((photo) => photo.id === over.id)
    if (from < 0 || to < 0) return

    const next = arrayMove(order, from, to)
    // Optimista: la grilla se acomoda al soltar y la escritura viaja detrás. Esperar la respuesta
    // para mover haría que cada arrastre se sienta trabado.
    const previous = order
    setOrder(next)
    reorder.mutate(
      next.map((photo) => photo.id),
      {
        // ☠️ EL ROLLBACK VA ACÁ Y NO EN LA INVALIDACIÓN. Invalidar tras el error refetchea, pero
        // el servidor devuelve LO MISMO que ya había —la escritura falló— y el structural sharing
        // de React Query conserva la MISMA referencia cuando el dato es igual. Con la referencia
        // intacta el `useMemo` no recalcula, el `useEffect` que resincroniza no dispara, y la
        // grilla se queda mostrando un orden que no existe. Verificado en el navegador.
        onError: () => {
          setOrder(previous)
        }
      }
    )
  }

  if (query.isLoading) {
    return (
      <PortfolioCanvas className="p-3">
        <div className="grid grid-cols-2 gap-3 @[420px]:grid-cols-3 @[560px]:grid-cols-4">
          <Skeleton className="aspect-square rounded-lg" />
          <Skeleton className="aspect-square rounded-lg" />
          <Skeleton className="aspect-square rounded-lg" />
          <Skeleton className="aspect-square rounded-lg" />
        </div>
      </PortfolioCanvas>
    )
  }

  if (query.isError) {
    return (
      <div className="p-3">
        <QueryErrorState
          error={query.error}
          resource="la galería"
          onRetry={() => {
            void query.refetch()
          }}
        />
      </div>
    )
  }

  const atLimit = order.length >= GALLERY_LIMIT
  const unpublished = order.filter((photo) => photo.visibility !== 'public').length

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-40 flex-1 text-xs text-muted-foreground">
          Las fotos que el cliente ve en la portada de {ownerLabel ?? 'este nivel'}. La primera es
          la portada; arrastra para cambiar el orden.
        </p>
        {/* Las acciones hacen `wrap` en vez de encoger: a 320 px el texto se lleva su renglón y
            los botones bajan enteros, en lugar de comprimirse hasta ser dos íconos sin rótulo. */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {extraActions}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5 transition-transform duration-150 ease-out active:scale-[0.97]"
            disabled={atLimit}
            onClick={() => {
              setQueue([])
              setUploadOpen(true)
            }}
            // El tope se dice en el botón y no al fallar: enterarse después de elegir el archivo
            // es enterarse tarde.
            title={atLimit ? `La galería admite ${GALLERY_LIMIT} fotos` : undefined}
          >
            <ImagePlus className="h-3.5 w-3.5" />
            Subir foto
          </Button>
        </div>
      </div>

      {/* Una foto en la galería pero SIN publicar no la ve nadie. Se avisa arriba porque es el
          error silencioso de esta pantalla: se acomoda todo y no llega al teléfono. */}
      {unpublished > 0 ? (
        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          {unpublished === 1
            ? 'Hay 1 foto sin publicar: el cliente todavía no la ve.'
            : `Hay ${unpublished} fotos sin publicar: el cliente todavía no las ve.`}
        </p>
      ) : null}

      {order.length === 0 ? (
        <Empty className="py-6">
          <EmptyHeader>
            <EmptyMedia>
              <Images className="h-6 w-6 text-muted-foreground" aria-hidden />
            </EmptyMedia>
            <EmptyTitle>Sin fotos en la galería</EmptyTitle>
            <EmptyDescription>
              La aplicación muestra un marcador hasta que haya al menos una publicada.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <PortfolioCanvas>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={order.map((photo) => photo.id)} strategy={rectSortingStrategy}>
              <ul className="grid grid-cols-2 gap-3 @[420px]:grid-cols-3 @[560px]:grid-cols-4">
                {order.map((photo, index) => (
                  <SortablePhoto
                    key={photo.id}
                    photo={photo}
                    index={index}
                    canPublish={canPublish}
                    disabled={reorder.isPending}
                    onRemove={(id) => {
                      setSlot.mutate({ id, slot: null })
                    }}
                    onToggleVisibility={(item) => {
                      setVisibility.mutate({
                        id: item.id,
                        visibility: item.visibility === 'public' ? 'internal' : 'public'
                      })
                    }}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </PortfolioCanvas>
      )}

      {/* Las imágenes ya subidas al cartapacio se agregan sin volver a subirlas: el mismo archivo
          sirve a los dos usos y duplicarlo sería dos copias que se despublican por separado. */}
      {candidates.length > 0 && !atLimit ? (
        <div className="flex flex-col gap-1 border-t border-border pt-2">
          <span className="text-xs font-medium text-muted-foreground">
            Imágenes del cartapacio que puedes agregar
          </span>
          <ul className="flex flex-wrap gap-1">
            {candidates.map((document) => (
              <li key={document.id}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 max-w-[14rem] gap-1 text-xs"
                  disabled={setSlot.isPending}
                  onClick={() => {
                    setSlot.mutate({ id: document.id, slot: 'gallery' })
                  }}
                >
                  <ImagePlus className="h-3 w-3 shrink-0" />
                  <span className="truncate">{document.name}</span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        ownerType={ownerType as CatalogOwnerType}
        // La galería NO clasifica: la foto va al carrusel de la portada, y la categoría es el eje
        // del cartapacio del cliente. Pedirla acá era pedir un dato que nadie lee.
        mode="photo"
        // Lo que QUEDA libre, no el tope entero: con nueve puestas sólo entran tres, y decirlo
        // antes de elegir evita que se seleccionen diez para que siete se rechacen después.
        maxFiles={GALLERY_LIMIT - order.length}
        canPublish={canPublish}
        publishReach={CATALOG_PUBLISH_REACH[ownerType as CatalogOwnerType]}
        pending={upload.isPending}
        categories={categories.data ?? []}
        queue={queue}
        onSubmit={(values) => {
          setQueue(createQueue(values.files))
          upload.mutate(values, {
            onSuccess: () => {
              setUploadOpen(false)
              setQueue([])
            }
          })
        }}
      />
    </div>
  )
}
